import type { ShopLocationDto } from "@babackapp/private-api-client/types";
import FormCountrySelector from "@babackapp/react-hook-forms-components/form-country-selector";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import FormSaveButton from "@babackapp/react-hook-forms-components/form-save-button";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import useTranslation from "@babackapp/translations/use-translation";
import { Form, FormField } from "@babackapp/ui/components/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { cn } from "@/utils/cn";
import {
	type LocationFormValues,
	locationFormSchema,
} from "./location-form-values";

type LocationFormProps = {
	onSubmit: (data: ShopLocationDto) => void;
};

const DEFAULT_VALUES = {
	isDefault: false,
	address1: "",
	address2: "",
	city: "",
	zip: "",
	companyName: "",
	country: {
		name: "France",
		code: "FR",
	},
};

export default function AddLocationForm({ onSubmit }: LocationFormProps) {
	const t = useTranslation();
	const defaultValues = DEFAULT_VALUES;
	const form = useForm<LocationFormValues>({
		resolver: zodResolver(locationFormSchema),
		defaultValues,
	});

	const _onSubmit = form.handleSubmit((data) => {
		onSubmit({
			isDefault: data.isDefault,
			address: {
				address1: data.address1,
				/**
				 * TODO: optional in API
				 */
				address2: data.address2 || "-",
				city: data.city,
				country_code: data.country.code,
				country_name: data.country.name,
				country: data.country.name,
				zip: data.zip,
				companyName: data.companyName,
			},
		});
	});

	return (
		<Form {...form}>
			<form
				onSubmit={_onSubmit}
				className="flex flex-col justify-start gap-8 mt-[16px]"
			>
				<FormField
					control={form.control}
					name="companyName"
					render={({ field }) => (
						<FormInput
							label={t("settings.shipping.fields.companyName.title")}
							description={t(
								"settings.shipping.fields.companyName.description",
							)}
							{...field}
						/>
					)}
				/>

				<div className="flex-col flex gap-8 w-full flex-wrap lg:flex-row">
					<FormField
						control={form.control}
						name="address1"
						render={({ field }) => (
							<FormInput
								label={t("settings.shipping.fields.address1.title")}
								{...field}
							/>
						)}
					/>
					<FormField
						control={form.control}
						name="address2"
						render={({ field }) => (
							<FormInput
								label={t("settings.shipping.fields.address2.title")}
								{...field}
							/>
						)}
					/>
				</div>
				<div className="flex-col flex gap-8 w-full flex-wrap lg:flex-row">
					<FormField
						control={form.control}
						name="city"
						render={({ field }) => (
							<FormInput
								label={t("settings.shipping.fields.city.title")}
								{...field}
							/>
						)}
					/>

					<FormField
						control={form.control}
						name="zip"
						render={({ field }) => (
							<FormInput
								label={t("settings.shipping.fields.zip.title")}
								{...field}
							/>
						)}
					/>
				</div>
				<FormField
					control={form.control}
					name="country"
					render={({ field }) => (
						<FormCountrySelector
							label={t("settings.shipping.fields.country.title")}
							value={field.value}
							onValueChange={field.onChange}
						/>
					)}
				/>

				<FormField
					control={form.control}
					name="isDefault"
					render={({ field }) => (
						<FormSwitch
							label={t("settings.shipping.fields.isDefault.title")}
							description={t("settings.shipping.fields.isDefault.description")}
							{...field}
						/>
					)}
				/>

				<div className={cn("flex items-center gap-2", "justify-center")}>
					<FormSaveButton />
				</div>
			</form>
		</Form>
	);
}
